import React, { useCallback } from "react";
import { defineChatTool, useUIEvents } from "@codenexus/react";
import type { ToolRendererProps } from "@codenexus/react";
import IngestionCard from "../chatComponents/IngestionCard";
import type { AppChatEventMap } from "./types";

/**
 * Tool-card Renderer for upload_file_to_knowledge_base. Renderers mount inside
 * the chat's ChatProvider tree, so useUIEvents() is available here: `emit`
 * reaches the host's onUIEvent handler, `pushContext` queues a context note the
 * agent sees on its next turn. The component owns the whole upload pipeline in
 * the browser — granular progress works despite the coarse tool `status`.
 */
export function UploadFileToKnowledgeBaseRenderer({ call }: ToolRendererProps): React.ReactElement {
    const bus = useUIEvents<AppChatEventMap>();
    const rawHint = (call.arguments as { filenameHint?: unknown } | undefined)?.filenameHint;
    const filenameHint = typeof rawHint === "string" && rawHint.trim().length > 0 ? rawHint.trim() : undefined;

    const handleFileIndexed = useCallback(
        (result: { fileId: number; filename: string }) => {
            bus.emit("knowledge_file_uploaded", result);
        },
        [bus]
    );

    const handleSettled = useCallback(
        (summary: { indexed: { fileId: number; filename: string }[]; failed: { filename: string; error: string }[] }) => {
            const indexedNotes = summary.indexed.map(f => `"${f.filename}" (fileId ${f.fileId})`);
            const failedNotes = summary.failed.map(f => `"${f.filename}" (${f.error})`);
            const parts: string[] = [];
            if (indexedNotes.length > 0) {
                parts.push(`Indexed and searchable: ${indexedNotes.join(", ")}.`);
            }
            if (failedNotes.length > 0) {
                parts.push(`Failed to ingest: ${failedNotes.join(", ")}.`);
            }
            bus.pushContext({
                knowledge_files_ingested: summary,
                note: `The in-chat uploader finished. ${parts.join(" ")}`.trim()
            });
        },
        [bus]
    );

    // Mirrors ingestionHostCard.tsx's onAborted handler (same note shape for
    // the `knowledge_ingestion_interrupted` context note). Duplicated rather
    // than shared so neither module's existing tests need to change.
    const handleAborted = useCallback(
        (summary: { indexed: { fileId: number; filename: string }[]; incomplete: string[] }) => {
            const indexedNotes = summary.indexed.map(f => `"${f.filename}" (fileId ${f.fileId})`);
            const incompleteNotes = summary.incomplete.map(name => `"${name}"`);
            const parts: string[] = [];
            if (indexedNotes.length > 0) {
                parts.push(`Already indexed and searchable: ${indexedNotes.join(", ")}.`);
            }
            if (incompleteNotes.length > 0) {
                parts.push(`Not completed: ${incompleteNotes.join(", ")}.`);
            }
            bus.pushContext({
                knowledge_ingestion_interrupted: summary,
                note: `Ingestion was interrupted by a chat-session change. ${parts.join(" ")}`.trim()
            });
        },
        [bus]
    );

    return (
        <IngestionCard
            allowPicker
            filenameHint={filenameHint}
            onFileIndexed={handleFileIndexed}
            onSettled={handleSettled}
            onAborted={handleAborted}
        />
    );
}

export const uploadFileToKnowledgeBaseTool = defineChatTool<{ filenameHint?: string }, AppChatEventMap>({
    name: "upload_file_to_knowledge_base",
    // "read": execute only mounts an interactive card; the browser performs the
    // upload. Mutation-category tools would trigger the approval prompt.
    category: "read",
    description:
        "Open an in-chat uploader card so the user can add one or more documents to their knowledge " +
        "base (vectorized for semantic search). Use when the user wants to upload, add, or ingest a " +
        "file/document. You cannot receive file bytes yourself — the card performs the upload in " +
        "the browser and accepts one or more files, dropped or chosen. After calling this, tell the " +
        "user to drop or choose their file(s) in the card. Results — including any files that failed " +
        "to ingest — arrive as a knowledge_files_ingested context note; do not claim the upload " +
        "finished before that note arrives.",
    parameters: {
        type: "object",
        properties: {
            filenameHint: {
                type: "string",
                description: "Optional filename the user mentioned. Display-only hint shown in the card before a file is chosen."
            }
        },
        required: [] as string[],
        additionalProperties: false
    },
    execute: async args => ({
        awaiting: true,
        filenameHint: typeof args.filenameHint === "string" && args.filenameHint.trim().length > 0 ? args.filenameHint.trim() : null,
        message:
            "Uploader card displayed in chat. The user will choose one or more files there. Do not claim " +
            "the ingestion finished — wait for a knowledge_files_ingested context note in a later turn."
    }),
    Renderer: UploadFileToKnowledgeBaseRenderer
});
